/**
 * One result from a chat — a table, a chart, a number, a document — in the
 * kit's `ResultFrame`, with the facts that matter and the actions that apply.
 *
 * Wiring only: which kit piece shows which kind of result, how its cells are
 * formatted, and where Download and Pin go. The look is all in `src/ui-kit`.
 * Documents still use the existing document card until it moves to the kit.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import type { ECharts } from 'echarts/core';
import DashboardCustomizeOutlined from '@mui/icons-material/DashboardCustomizeOutlined';
import { format as formatSQL, type FormatOptionsWithLanguage } from 'sql-formatter';
import {
  Chart,
  CodeBlock,
  DownloadButton,
  EmptyState,
  MetricCard,
  ResultFrame,
  ResultTable,
  ToolbarButton,
  type ResultColumn,
} from '../../ui-kit';
import type {
  ChartArtifact,
  MetricArtifact,
  QueryResultTab,
  WorkspaceArtifact,
} from '../../pages/copilot/stores/useCopilotStore';
import { getArtifactHydrationState } from '../../pages/copilot/hooks/useArtifactHydration';
import { DocumentCard } from '../../pages/copilot/components/WorkspacePanel';
import { buildResultColumns } from '../../components/table/resultColumns';
import { formatNumber, formatPercent } from '../../components/table/format';
import { downloadFrame, fetchFramePage } from '../../utils/frameExport';
import { downloadChartImage, type ChartImageFormat } from '../../utils/chartDownload';
import { formatRelativeTime } from '../../utils/date';
import { SnackbarContext } from '../../ui/SnackbarProvider';

export interface ResultViewProps {
  artifact: WorkspaceArtifact;
  /** `pane` — fills the page's results pane. `card` — a card inside the conversation. */
  variant: 'pane' | 'card';
  /** The chat the result belongs to; needed to fetch more rows and download. */
  threadId: string | null;
  /** Show the result's name in the frame — for cards; the pane names it above. */
  showTitle?: boolean;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** Load a result that so far is only a reference (from an earlier session). */
  onHydrate?: (artifactId: string) => void;
}

const CHART_HEIGHT = { pane: 420, card: 280 } as const;
const TABLE_MAX_HEIGHT_IN_CARD = 360;

const rows = (n: number) => `${n.toLocaleString()} row${n === 1 ? '' : 's'}`;
const ago = (iso?: string) => (iso ? formatRelativeTime(iso) : '');

function sqlLabel(a: QueryResultTab): string {
  if (a.isFileConnector) return 'Source';
  const cat = (a.connectorCategory || '').toLowerCase();
  return cat === 'api' || cat === 'rest' ? 'Request' : 'SQL';
}

/** The formatter's name for a connector's SQL flavour; plain SQL when unknown. */
const SQL_DIALECTS: Array<[RegExp, FormatOptionsWithLanguage['language']]> = [
  [/redshift/i, 'redshift'],
  [/snowflake/i, 'snowflake'],
  [/bigquery/i, 'bigquery'],
  [/postgres/i, 'postgresql'],
  [/mysql/i, 'mysql'],
  [/mariadb/i, 'mariadb'],
  [/sql\s*server|mssql|azure/i, 'transactsql'],
  [/databricks|spark/i, 'spark'],
  [/trino|presto|athena/i, 'trino'],
  [/sqlite/i, 'sqlite'],
];

/** A query laid out to read: its warehouse's flavour, keywords in capitals, two-space indent. */
function readableQuery(a: QueryResultTab): { code: string; language: 'sql' | 'json' } {
  const text = a.sql || '';
  if (sqlLabel(a) !== 'SQL') {
    try {
      return { code: JSON.stringify(JSON.parse(text), null, 2), language: 'json' };
    } catch {
      return { code: text, language: 'json' };
    }
  }
  const dialect = SQL_DIALECTS.find(([re]) => re.test(a.connectorType || ''))?.[1] ?? 'sql';
  const options = { keywordCase: 'upper', tabWidth: 2, expressionWidth: 60 } as const;
  try {
    return { code: formatSQL(text, { ...options, language: dialect }), language: 'sql' };
  } catch {
    try {
      // A flavour the formatter reads differently: plain SQL still lays it out.
      return { code: formatSQL(text, { ...options, language: 'sql' }), language: 'sql' };
    } catch {
      return { code: text, language: 'sql' };
    }
  }
}

/** A metric's value, formatted as its name suggests; the artifact carries no unit. */
function metricValue(a: MetricArtifact): string {
  const v = a.metricValue;
  if (typeof v !== 'number') return v === null || v === undefined ? '—' : String(v);
  const name = `${a.metricName} ${a.title}`.toLowerCase();
  if (/(pct|percent|rate|ratio|share)/.test(name)) return formatPercent(v, Math.abs(v) <= 1 ? 'fraction' : 'whole');
  return formatNumber(v);
}

export default function ResultView({
  artifact,
  variant,
  threadId,
  showTitle = variant === 'card',
  onAddToDashboard,
  onHydrate,
}: ResultViewProps) {
  const { notify } = useContext(SnackbarContext);
  const hydration = getArtifactHydrationState(artifact);
  const [view, setView] = useState('table');
  const chartRef = useRef<ECharts | null>(null);

  // In the pane a result is on screen, so load it; in a card, wait to be asked.
  useEffect(() => {
    if (variant === 'pane' && hydration === 'stub') onHydrate?.(artifact.id);
  }, [variant, hydration, artifact.id, onHydrate]);
  useEffect(() => setView('table'), [artifact.id]);

  const title = showTitle ? artifact.title : undefined;

  // In a narrow frame the button keeps its icon and drops its words.
  const pin = (compact: boolean) =>
    onAddToDashboard && (
      <ToolbarButton
        icon={<DashboardCustomizeOutlined fontSize="small" />}
        onClick={() => onAddToDashboard(artifact)}
        aria-label="Pin to dashboard"
      >
        {compact ? undefined : 'Pin to dashboard'}
      </ToolbarButton>
    );

  if (hydration !== 'ready') {
    const failed = hydration === 'error';
    return (
      <ResultFrame title={artifact.title} variant={variant}>
        {hydration === 'stub' && variant === 'card' && onHydrate ? (
          <Button size="small" variant="outlined" onClick={() => onHydrate(artifact.id)}>
            Show result
          </Button>
        ) : (
          <EmptyState
            message={failed ? artifact.hydrationError || 'This result could not be loaded.' : `Loading ${artifact.title}…`}
            height={variant === 'pane' ? 240 : 120}
          />
        )}
      </ResultFrame>
    );
  }

  switch (artifact.type) {
    case 'query_result':
      return (
        <QueryResult
          artifact={artifact}
          variant={variant}
          title={title}
          threadId={threadId}
          view={view}
          onViewChange={setView}
          actions={(compact) => (
            <>
              {artifact.frameId && threadId && (
                <DownloadButton
                  compact={compact}
                  formats={(['xlsx', 'csv'] as const).map((fmt) => ({
                    key: fmt,
                    label: fmt === 'xlsx' ? 'Excel (.xlsx)' : 'CSV',
                    description: `All ${rows(artifact.rowCount)}`,
                    onDownload: async () => {
                      notify(`Preparing ${fmt === 'xlsx' ? 'Excel' : 'CSV'}…`, 'info');
                      try {
                        const { filename } = await downloadFrame(artifact.frameId!, threadId, fmt, artifact.title);
                        notify(`Downloaded ${filename}`, 'success');
                      } catch (e) {
                        notify(e instanceof Error ? e.message : 'The download failed.', 'error');
                      }
                    },
                  }))}
                />
              )}
              {pin(compact)}
            </>
          )}
        />
      );
    case 'chart': {
      const a = artifact as ChartArtifact;
      const download = (fmt: ChartImageFormat) => {
        try {
          downloadChartImage(chartRef.current, fmt, a.title);
        } catch (e) {
          notify(e instanceof Error ? e.message : 'The chart could not be downloaded.', 'error');
        }
      };
      return (
        <ResultFrame
          title={title}
          variant={variant}
          meta={[a.rowCount ? rows(a.rowCount) : '', ago(a.createdAt)]}
          expanded={{
            title: a.title || 'Chart',
            content: <Chart option={a.spec} fill ariaLabel={a.title || 'Chart'} />,
          }}
          actions={(compact) => (
            <>
              <DownloadButton
                compact={compact}
                formats={[
                  { key: 'png', label: 'Image (.png)', onDownload: () => download('png') },
                  { key: 'svg', label: 'Vector (.svg)', description: 'Sharp at any size', onDownload: () => download('svg') },
                ]}
              />
              {pin(compact)}
            </>
          )}
        >
          <Chart
            option={a.spec}
            height={CHART_HEIGHT[variant]}
            onReady={(c) => {
              chartRef.current = c;
            }}
            ariaLabel={a.title || 'Chart'}
          />
        </ResultFrame>
      );
    }
    case 'metric_card': {
      const a = artifact as MetricArtifact;
      return (
        <ResultFrame title={title} variant={variant} meta={[ago(a.createdAt)]} actions={(compact) => pin(compact)}>
          <MetricCard label={a.metricName || a.title} value={metricValue(a)} size={variant === 'pane' ? 'hero' : 'tile'} bare />
        </ResultFrame>
      );
    }
    case 'document':
      return (
        <ResultFrame title={title} variant={variant} meta={[artifact.format?.toUpperCase() ?? '', ago(artifact.createdAt)]}>
          <DocumentCard artifact={artifact} />
        </ResultFrame>
      );
    default:
      return null;
  }
}

/** A query's rows — sortable, paged, formatted by column — or the query itself. */
function QueryResult({
  artifact,
  variant,
  title,
  threadId,
  view,
  onViewChange,
  actions,
}: {
  artifact: QueryResultTab;
  variant: 'pane' | 'card';
  title?: string;
  threadId: string | null;
  view: string;
  onViewChange: (v: string) => void;
  actions: (compact: boolean) => React.ReactNode;
}) {
  const columns: ResultColumn[] = useMemo(
    () =>
      buildResultColumns(artifact.columns, artifact.rows, {
        columnTypes: artifact.columnTypes,
        columnSemantics: artifact.columnSemantics,
      }),
    [artifact.columns, artifact.rows, artifact.columnTypes, artifact.columnSemantics],
  );

  const onLoadPage =
    artifact.frameId && threadId
      ? (page: number, size: number) => fetchFramePage(artifact.frameId!, threadId, page, size)
      : undefined;

  const label = sqlLabel(artifact);
  const hasQuery = Boolean(artifact.sql?.trim());

  return (
    <ResultFrame
      title={title}
      variant={variant}
      meta={[
        rows(artifact.rowCount),
        artifact.connectorName,
        ago(artifact.createdAt),
      ]}
      views={hasQuery ? [{ value: 'table', label: 'Table' }, { value: 'query', label }] : undefined}
      view={view}
      onViewChange={onViewChange}
      actions={actions}
      flush={view !== 'query' || !hasQuery}
      expanded={{
        title: artifact.title || 'Result',
        content: (
          <ResultTable
            columns={columns}
            rows={artifact.rows}
            totalRows={artifact.rowCount}
            onLoadPage={onLoadPage}
            fill
            ariaLabel={artifact.title || 'Result'}
          />
        ),
      }}
    >
      {view === 'query' && hasQuery ? (
        <CodeBlock {...readableQuery(artifact)} lineNumbers copyable aria-label={label} />
      ) : (
        <ResultTable
          columns={columns}
          rows={artifact.rows}
          totalRows={artifact.rowCount}
          onLoadPage={onLoadPage}
          maxHeight={variant === 'card' ? TABLE_MAX_HEIGHT_IN_CARD : undefined}
          fill={variant === 'pane'}
          ariaLabel={artifact.title || 'Result'}
        />
      )}
    </ResultFrame>
  );
}
